import { useState, useRef, useCallback } from 'react';
import { FunctionEntry } from '@/types/simulation';
import { analyzeImageToFormula, ImageAnalysisResult, ExtractedCurve } from '@/utils/imageToFormula';
import { importFromImage } from '@/utils/fileImport';
import { useTheme } from '@/contexts/ThemeContext';

interface BatchItem {
  id: string;
  file: File;
  previewUrl: string;
  width: number;
  height: number;
  status: 'pending' | 'analyzing' | 'done' | 'error';
  result: ImageAnalysisResult | null;
  errorMsg: string | null;
  selectedCurves: Set<number>;
}

interface Props {
  onImport: (functions: Omit<FunctionEntry, 'id'>[]) => void;
}

const PRESET_COLORS = ['#00d9ff', '#ff6b35', '#ffd700', '#ff4da6', '#7fff00', '#c084fc', '#ff8c00', '#00ff88', '#ff3366', '#ffaa00'];
let colorOffset = 0;
function pickColor() {
  return PRESET_COLORS[colorOffset++ % PRESET_COLORS.length];
}

const typeLabel: Record<ExtractedCurve['type'], string> = {
  polynomial: '多項式',
  sinusoidal: '正弦波',
  exponential: '指数',
  raw: '近似',
};

export default function BatchImageAnalyzer({ onImport }: Props) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const [items, setItems] = useState<BatchItem[]>([]);
  const [dragging, setDragging] = useState(false);
  const [xRangeMin, setXRangeMin] = useState(-10);
  const [xRangeMax, setXRangeMax] = useState(10);
  const [yRangeMin, setYRangeMin] = useState(-10);
  const [yRangeMax, setYRangeMax] = useState(10);
  const [isAnalyzingAll, setIsAnalyzingAll] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const textPrimary   = isDark ? 'text-white'    : 'text-gray-900';
  const textSecondary = isDark ? 'text-white/60' : 'text-gray-600';
  const textMuted     = isDark ? 'text-white/35' : 'text-gray-400';
  const borderSubtle  = isDark ? 'border-white/10' : 'border-gray-200';

  // ─── ファイル追加 ────────────────────────────────────────────────

  const addFiles = useCallback(async (files: File[]) => {
    const imageFiles = files.filter((f) => f.type.startsWith('image/'));
    if (imageFiles.length === 0) return;

    const newItems: BatchItem[] = await Promise.all(
      imageFiles.map(async (file) => {
        try {
          const img = await importFromImage(file);
          return {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file,
            previewUrl: img.dataUrl,
            width: img.width,
            height: img.height,
            status: 'pending' as const,
            result: null,
            errorMsg: null,
            selectedCurves: new Set<number>(),
          };
        } catch {
          return {
            id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
            file,
            previewUrl: '',
            width: 0,
            height: 0,
            status: 'error' as const,
            result: null,
            errorMsg: '画像の読み込みに失敗しました',
            selectedCurves: new Set<number>(),
          };
        }
      })
    );

    setItems((prev) => [...prev, ...newItems]);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  }, [addFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    addFiles(files);
    e.target.value = '';
  }, [addFiles]);

  const removeItem = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
    if (expandedId === id) setExpandedId(null);
  };

  // ─── 解析処理 ────────────────────────────────────────────────────

  const analyzeItem = useCallback(async (id: string) => {
    setItems((prev) =>
      prev.map((item) => item.id === id ? { ...item, status: 'analyzing' } : item)
    );

    const item = items.find((i) => i.id === id);
    if (!item) return;

    try {
      const result = await analyzeImageToFormula(
        item.file,
        [xRangeMin, xRangeMax],
        [yRangeMin, yRangeMax],
      );
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? {
                ...i,
                status: 'done',
                result,
                selectedCurves: new Set(result.curves.map((_, idx) => idx)),
              }
            : i
        )
      );
      setExpandedId(id);
    } catch (err) {
      setItems((prev) =>
        prev.map((i) =>
          i.id === id
            ? { ...i, status: 'error', errorMsg: err instanceof Error ? err.message : '解析失敗' }
            : i
        )
      );
    }
  }, [items, xRangeMin, xRangeMax, yRangeMin, yRangeMax]);

  const analyzeAll = useCallback(async () => {
    const pending = items.filter((i) => i.status === 'pending' || i.status === 'error');
    if (pending.length === 0) return;

    setIsAnalyzingAll(true);

    // 並列で解析（最大3並列）
    const chunkSize = 3;
    for (let i = 0; i < pending.length; i += chunkSize) {
      const chunk = pending.slice(i, i + chunkSize);
      await Promise.all(chunk.map((item) => analyzeItem(item.id)));
    }

    setIsAnalyzingAll(false);
  }, [items, analyzeItem]);

  // ─── 選択操作 ────────────────────────────────────────────────────

  const toggleCurve = (itemId: string, curveIdx: number) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId) return item;
        const next = new Set(item.selectedCurves);
        if (next.has(curveIdx)) next.delete(curveIdx);
        else next.add(curveIdx);
        return { ...item, selectedCurves: next };
      })
    );
  };

  const selectAllCurves = (itemId: string, select: boolean) => {
    setItems((prev) =>
      prev.map((item) => {
        if (item.id !== itemId || !item.result) return item;
        return {
          ...item,
          selectedCurves: select
            ? new Set(item.result.curves.map((_, i) => i))
            : new Set<number>(),
        };
      })
    );
  };

  // ─── インポート ──────────────────────────────────────────────────

  const totalSelected = items.reduce((sum, item) => {
    if (!item.result) return sum;
    return sum + item.selectedCurves.size;
  }, 0);

  const handleImportAll = () => {
    colorOffset = 0;
    const fns: Omit<FunctionEntry, 'id'>[] = [];
    for (const item of items) {
      if (!item.result) continue;
      item.result.curves.forEach((curve, i) => {
        if (item.selectedCurves.has(i)) {
          fns.push({ expression: curve.formula, color: pickColor(), visible: true });
        }
      });
    }
    onImport(fns);
  };

  // ─── ステータスバッジ ────────────────────────────────────────────

  const StatusBadge = ({ status, curveCount }: { status: BatchItem['status']; curveCount: number }) => {
    if (status === 'pending') return (
      <span className={`text-xs ${textMuted} flex items-center gap-1`}>
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-time-line text-xs" />
        </div>
        待機中
      </span>
    );
    if (status === 'analyzing') return (
      <span className="text-xs text-yellow-400 flex items-center gap-1">
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-loader-4-line text-xs animate-spin" />
        </div>
        解析中...
      </span>
    );
    if (status === 'error') return (
      <span className="text-xs text-red-400 flex items-center gap-1">
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-error-warning-line text-xs" />
        </div>
        エラー
      </span>
    );
    return (
      <span className="text-xs text-emerald-400 flex items-center gap-1">
        <div className="w-3 h-3 flex items-center justify-center">
          <i className="ri-check-line text-xs" />
        </div>
        {curveCount}件検出
      </span>
    );
  };

  const R2Badge = ({ r2 }: { r2: number }) => {
    const pct = Math.round(r2 * 100);
    const color = r2 >= 0.85 ? 'text-emerald-400' : r2 >= 0.5 ? 'text-yellow-400' : 'text-red-400';
    return <span className={`text-xs font-mono ${color}`}>R²={pct}%</span>;
  };

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const doneCount = items.filter((i) => i.status === 'done').length;

  return (
    <div className="space-y-3">
      {/* ドロップゾーン */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`glass rounded-xl p-6 flex flex-col items-center justify-center gap-2 cursor-pointer transition-all duration-200 border-2 border-dashed ${
          dragging
            ? 'border-cyan-400/60 bg-cyan-400/5'
            : `${isDark ? 'border-white/10' : 'border-gray-200'} hover:border-cyan-400/40`
        }`}
      >
        <div className="w-10 h-10 flex items-center justify-center rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.15), rgba(0,130,180,0.08))', border: '1px solid rgba(0,180,220,0.20)' }}>
          <i className="ri-image-add-line text-xl" style={{ color: 'var(--accent-cyan)' }} />
        </div>
        <div className="text-center">
          <p className={`${textPrimary} text-sm font-medium`}>複数の画像をドロップ</p>
          <p className={`${textMuted} text-xs mt-0.5`}>または クリックして複数選択（PNG / JPG / WebP）</p>
        </div>
        {items.length > 0 && (
          <div className="flex items-center gap-3 mt-1">
            <span className={`glass px-2 py-0.5 rounded-full text-xs ${textMuted}`}>{items.length}枚追加済み</span>
            {doneCount > 0 && <span className="glass px-2 py-0.5 rounded-full text-xs text-emerald-400">{doneCount}枚解析完了</span>}
          </div>
        )}
      </div>
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />

      {/* グラフ範囲設定（共通） */}
      {items.length > 0 && (
        <div className="glass rounded-xl p-3 space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-settings-3-line text-sm" style={{ color: 'var(--accent-cyan)' }} />
            </div>
            <span className={`${textPrimary} text-xs font-medium`}>共通グラフ範囲</span>
            <span className={`${textMuted} text-xs`}>（全画像に適用）</span>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className={`${textMuted} text-xs`}>X軸</p>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={xRangeMin}
                  onChange={(e) => setXRangeMin(Number(e.target.value))}
                  className={`glass flex-1 min-w-0 px-2 py-1 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                />
                <span className={`${textMuted} text-xs`}>〜</span>
                <input
                  type="number"
                  value={xRangeMax}
                  onChange={(e) => setXRangeMax(Number(e.target.value))}
                  className={`glass flex-1 min-w-0 px-2 py-1 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                />
              </div>
            </div>
            <div className="space-y-1">
              <p className={`${textMuted} text-xs`}>Y軸</p>
              <div className="flex items-center gap-1.5">
                <input
                  type="number"
                  value={yRangeMin}
                  onChange={(e) => setYRangeMin(Number(e.target.value))}
                  className={`glass flex-1 min-w-0 px-2 py-1 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                />
                <span className={`${textMuted} text-xs`}>〜</span>
                <input
                  type="number"
                  value={yRangeMax}
                  onChange={(e) => setYRangeMax(Number(e.target.value))}
                  className={`glass flex-1 min-w-0 px-2 py-1 rounded-lg text-xs ${textPrimary} bg-transparent outline-none text-center`}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 画像リスト */}
      {items.length > 0 && (
        <div className="space-y-2 max-h-[340px] overflow-y-auto custom-scrollbar pr-0.5">
          {items.map((item) => (
            <div key={item.id} className="glass rounded-xl overflow-hidden">
              {/* カードヘッダー */}
              <div className="flex items-center gap-3 px-3 py-2.5">
                {/* サムネイル */}
                <div className="w-12 h-10 flex-shrink-0 rounded-lg overflow-hidden bg-black/20">
                  {item.previewUrl ? (
                    <img src={item.previewUrl} alt={item.file.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <i className="ri-image-line text-sm text-white/20" />
                    </div>
                  )}
                </div>

                {/* ファイル情報 */}
                <div className="flex-1 min-w-0">
                  <p className={`${textPrimary} text-xs font-medium truncate`}>{item.file.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className={`${textMuted} text-xs`}>{item.width}×{item.height}</span>
                    <StatusBadge
                      status={item.status}
                      curveCount={item.result?.curves.length ?? 0}
                    />
                  </div>
                </div>

                {/* アクションボタン */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  {item.status === 'done' && item.result && item.result.curves.length > 0 && (
                    <button
                      onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                      className={`glass-btn ripple-btn w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${textMuted} hover:${textSecondary} transition-all`}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-xs ${expandedId === item.id ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}`} />
                      </div>
                    </button>
                  )}
                  {(item.status === 'pending' || item.status === 'error') && (
                    <button
                      onClick={() => analyzeItem(item.id)}
                      className="glass-btn ripple-btn px-2 py-1 rounded-lg text-xs cursor-pointer whitespace-nowrap transition-all"
                      style={{ color: 'var(--accent-cyan)' }}
                    >
                      解析
                    </button>
                  )}
                  {item.status === 'done' && (
                    <button
                      onClick={() => {
                        setItems((prev) =>
                          prev.map((i) => i.id === item.id ? { ...i, status: 'pending', result: null } : i)
                        );
                      }}
                      className={`glass-btn ripple-btn px-2 py-1 rounded-lg text-xs cursor-pointer whitespace-nowrap ${textMuted} hover:${textSecondary} transition-all`}
                    >
                      再解析
                    </button>
                  )}
                  <button
                    onClick={() => removeItem(item.id)}
                    className={`glass-btn ripple-btn w-7 h-7 flex items-center justify-center rounded-lg cursor-pointer ${textMuted} hover:text-red-400 transition-all`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-close-line text-xs" />
                    </div>
                  </button>
                </div>
              </div>

              {/* エラーメッセージ */}
              {item.status === 'error' && item.errorMsg && (
                <div className={`px-3 pb-2.5 border-t ${borderSubtle}`}>
                  <p className="text-red-400 text-xs mt-2">{item.errorMsg}</p>
                </div>
              )}

              {/* 解析結果（展開時） */}
              {expandedId === item.id && item.result && (
                <div className={`border-t ${borderSubtle} p-3 space-y-2 animate-float-in`}>
                  {/* プレビュー（検出点付き） */}
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={item.result.previewDataUrl}
                      alt="analysis"
                      className="w-full object-contain max-h-28"
                    />
                  </div>

                  {/* メッセージ */}
                  <p className={`text-xs ${item.result.curves.length > 0 ? 'text-emerald-400' : 'text-yellow-400'}`}>
                    {item.result.message}
                  </p>

                  {/* 数式リスト */}
                  {item.result.curves.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <span className={`${textMuted} text-xs`}>{item.result.curves.length}件の数式</span>
                        <div className="flex gap-1">
                          <button
                            onClick={() => selectAllCurves(item.id, true)}
                            className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                          >
                            全選択
                          </button>
                          <button
                            onClick={() => selectAllCurves(item.id, false)}
                            className={`glass-btn ripple-btn text-xs ${textMuted} hover:${textSecondary} px-1.5 py-0.5 rounded cursor-pointer whitespace-nowrap`}
                          >
                            解除
                          </button>
                        </div>
                      </div>
                      <div className="space-y-1.5">
                        {item.result.curves.map((curve, ci) => (
                          <button
                            key={ci}
                            onClick={() => toggleCurve(item.id, ci)}
                            className={`glass ripple-btn w-full flex items-start gap-2 px-2.5 py-2 rounded-lg cursor-pointer transition-all text-left ${
                              item.selectedCurves.has(ci) ? 'border-cyan-400/40' : 'opacity-50'
                            }`}
                          >
                            <div className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-0.5" style={{ backgroundColor: PRESET_COLORS[ci % PRESET_COLORS.length] }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <span className={`glass px-1 py-0.5 rounded text-xs ${isDark ? 'text-cyan-300/70' : 'text-sky-600/70'}`}>
                                  {typeLabel[curve.type]}
                                </span>
                                <R2Badge r2={curve.r2} />
                              </div>
                              <p className={`${textPrimary} text-xs font-mono mt-0.5 break-all`}>
                                y = {curve.formula}
                              </p>
                            </div>
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                              <i className={`text-sm ${item.selectedCurves.has(ci) ? 'ri-checkbox-circle-fill text-cyan-400' : 'ri-checkbox-blank-circle-line text-white/20'}`} />
                            </div>
                          </button>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p className={`${textMuted} text-xs`}>数式を検出できませんでした。グラフ範囲を調整して再解析してください。</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* アクションバー */}
      {items.length > 0 && (
        <div className="space-y-2">
          {/* 進捗サマリー */}
          <div className={`glass rounded-xl px-3 py-2 flex items-center justify-between`}>
            <div className="flex items-center gap-3">
              <span className={`${textMuted} text-xs`}>{items.length}枚</span>
              {doneCount > 0 && (
                <span className="text-xs text-emerald-400">{doneCount}枚完了</span>
              )}
              {pendingCount > 0 && (
                <span className={`text-xs ${textMuted}`}>{pendingCount}枚待機中</span>
              )}
              {totalSelected > 0 && (
                <span className="text-xs" style={{ color: 'var(--accent-cyan)' }}>
                  {totalSelected}件の数式を選択中
                </span>
              )}
            </div>
            <button
              onClick={() => setItems([])}
              className={`glass-btn ripple-btn text-xs ${textMuted} hover:text-red-400 px-2 py-0.5 rounded cursor-pointer whitespace-nowrap`}
            >
              全削除
            </button>
          </div>

          <div className="flex gap-2">
            {/* 全て解析ボタン */}
            {pendingCount > 0 && (
              <button
                onClick={analyzeAll}
                disabled={isAnalyzingAll}
                className="glass-btn ripple-btn flex-1 py-2.5 rounded-xl text-sm font-medium cursor-pointer whitespace-nowrap transition-all disabled:opacity-60"
                style={{ color: 'var(--accent-cyan)', border: '1px solid rgba(0,180,220,0.3)' }}
              >
                {isAnalyzingAll ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-loader-4-line text-sm animate-spin" />
                    </div>
                    解析中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-magic-line text-sm" />
                    </div>
                    {pendingCount}枚を一括解析
                  </span>
                )}
              </button>
            )}

            {/* 一括インポートボタン */}
            {totalSelected > 0 && (
              <button
                onClick={handleImportAll}
                className="ripple-btn flex-1 py-2.5 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap text-white transition-all"
                style={{ background: 'linear-gradient(135deg, rgba(0,180,220,0.7), rgba(0,130,180,0.6))', border: '1px solid rgba(0,180,220,0.4)' }}
              >
                <span className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-add-line text-sm" />
                  </div>
                  {totalSelected}件をグラフに追加
                </span>
              </button>
            )}
          </div>
        </div>
      )}

      {/* 空状態 */}
      {items.length === 0 && (
        <div className="glass rounded-xl p-3 space-y-1.5">
          <p className={`${textMuted} text-xs font-medium`}>使い方</p>
          {[
            '複数の画像を一度にドロップまたは選択',
            '「一括解析」で全画像を自動解析',
            '各画像の数式を選択してグラフに追加',
          ].map((tip, i) => (
            <div key={i} className="flex items-start gap-2">
              <span className={`glass px-1.5 py-0.5 rounded text-xs font-mono ${isDark ? 'text-cyan-300' : 'text-sky-600'} flex-shrink-0`}>{i + 1}</span>
              <span className={`${textMuted} text-xs`}>{tip}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
